import { NavLink, useNavigate } from 'react-router-dom'
import { useAuthStore, useUiStore } from '../../stores'
import { UserInfoSkeleton } from '../common'
import { signOutSafely } from '../../lib/supabase'
import { useEffect } from 'react'

interface NavItem {
  label: string
  href: string
  icon: JSX.Element
  requiresUpload?: boolean
  requiresPaidTier?: boolean
  requiresCorporate?: boolean
}

// Navigation sections for better organization
interface NavSection {
  title?: string
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    // Main section - no title
    items: [
      {
        label: 'Dashboard',
        href: '/dashboard',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
            />
          </svg>
        ),
      },
      {
        label: 'Analytics',
        href: '/dashboard/analytics',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
    ],
  },
  {
    title: 'Library',
    items: [
      {
        label: 'Videos',
        href: '/dashboard/videos',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Programs',
    items: [
      {
        label: 'My Programs',
        href: '/dashboard/programs',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
      {
        label: 'Templates',
        href: '/dashboard/templates',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
      },
      {
        label: 'Assignments',
        href: '/dashboard/assignments',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
      {
        label: 'Progress',
        href: '/dashboard/progress',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
    ],
  },
  {
    title: 'Clients',
    items: [
      {
        label: 'All Clients',
        href: '/dashboard/clients',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
      {
        label: 'Groups',
        href: '/dashboard/groups',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        ),
        requiresPaidTier: true,
      },
    ],
  },
  {
    title: 'Team',
    items: [
      {
        label: 'Team Members',
        href: '/dashboard/team',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        requiresCorporate: true,
      },
    ],
  },
]

export function Sidebar() {
  const navigate = useNavigate()
  const { canUploadVideos, profile, _hasHydrated, logout } = useAuthStore()
  const { isSidebarOpen, isSidebarCollapsed, toggleSidebar, setSidebarOpen } = useUiStore()

  // Handle sign out
  const handleSignOut = async () => {
    setSidebarOpen(false)
    await signOutSafely()
    logout()
    navigate('/', { replace: true })
  }

  // Handle nav item click - close sidebar on mobile
  const handleNavClick = () => {
    // Only close on mobile (< lg breakpoint)
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(false)
    }
  }

  // Body scroll lock when sidebar is open on mobile
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isSidebarOpen && window.innerWidth < 1024) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isSidebarOpen])

  const hasPaidTier = profile?.subscription_tier && profile.subscription_tier !== 'free'
  const hasCorporateTier = profile?.subscription_tier === 'corporate' || profile?.subscription_tier === 'platinum'

  const filterItem = (item: NavItem): boolean => {
    if (item.requiresUpload && !canUploadVideos()) return false
    if (item.requiresPaidTier && !hasPaidTier) return false
    if (item.requiresCorporate && !hasCorporateTier) return false
    return true
  }

  const filteredSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter(filterItem),
    }))
    .filter((section) => section.items.length > 0)

  return (
    <>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-16 left-0 h-[calc(100vh-4rem)] bg-white border-r border-gray-200 z-40
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isSidebarCollapsed ? 'w-16' : 'w-64'}
          lg:translate-x-0
        `}
      >
        {/* Close button - mobile only */}
        <button
          className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors z-50"
          onClick={toggleSidebar}
          aria-label="Close sidebar"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="flex flex-col h-full">
          {/* User info */}
          <div className={`p-4 border-b border-gray-200 ${isSidebarCollapsed ? 'hidden' : ''}`}>
            {(!_hasHydrated || !profile) ? (
              <UserInfoSkeleton />
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                  {profile?.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.full_name || 'Profile'}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-medium text-gray-600">
                      {profile?.full_name?.[0]?.toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {profile?.full_name || 'User'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        profile?.subscription_status === 'past_due'
                          ? 'bg-red-100 text-red-700'
                          : profile?.subscription_status === 'canceling'
                            ? 'bg-yellow-100 text-yellow-700'
                            : !profile?.subscription_tier || profile.subscription_tier === 'free'
                              ? 'bg-gray-100 text-gray-600'
                              : 'bg-blue-100 text-blue-700'
                      }`}
                    >
                      {(profile?.subscription_tier || 'free').charAt(0).toUpperCase() +
                        (profile?.subscription_tier || 'free').slice(1)}
                    </span>
                    {profile?.subscription_status === 'past_due' && (
                      <span className="text-xs text-red-600 font-medium">Past Due</span>
                    )}
                    {profile?.subscription_status === 'canceling' && (
                      <span className="text-xs text-yellow-600 font-medium">Canceling</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
            {filteredSections.map((section, sectionIndex) => (
              <div key={sectionIndex}>
                {section.title && !isSidebarCollapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {section.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      end={item.href === '/dashboard'}
                      onClick={handleNavClick}
                      className={({ isActive }) => `
                        flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                        ${
                          isActive
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                        ${isSidebarCollapsed ? 'justify-center' : ''}
                      `}
                      title={isSidebarCollapsed ? item.label : undefined}
                    >
                      {item.icon}
                      {!isSidebarCollapsed && <span>{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Upgrade CTA (for non-advanced users) */}
          {!canUploadVideos() && !isSidebarCollapsed && (
            <div className="p-4 border-t border-gray-200">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-1">
                  Upgrade to Upload
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Get Advanced plan to upload your own videos.
                </p>
                <NavLink
                  to="/dashboard/subscription"
                  onClick={handleNavClick}
                  className="text-sm font-medium text-blue-600 hover:text-blue-800"
                >
                  View Plans &rarr;
                </NavLink>
              </div>
            </div>
          )}

          {/* Bottom Navigation */}
          <div className={`p-4 border-t border-gray-200 mt-auto ${isSidebarCollapsed ? 'px-2' : ''}`}>
            <div className="space-y-1">
              <NavLink
                to="/dashboard/profile"
                onClick={handleNavClick}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                  ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                  ${isSidebarCollapsed ? 'justify-center' : ''}
                `}
                title={isSidebarCollapsed ? 'Settings' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {!isSidebarCollapsed && <span>Settings</span>}
              </NavLink>
              <button
                onClick={handleSignOut}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors w-full ${isSidebarCollapsed ? 'justify-center' : ''}`}
                title={isSidebarCollapsed ? 'Sign Out' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {!isSidebarCollapsed && <span>Sign Out</span>}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
